import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import axios from 'axios';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlined from '@mui/icons-material/ErrorOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import { formatPhone, isValidEmail, formatAirportCode } from '../utils/formatters';
import API_BASE from '../config/api';

const TABS = ['Identity', 'Travel & Loyalty', 'Documents', 'Medical', 'Emergency Contacts'];

const BUNK_OPTIONS = [
  'Driver Forward Top',
  'Driver Forward Middle',
  'Driver Forward Bottom',
  'Driver Rear Top',
  'Driver Rear Middle',
  'Driver Rear Bottom',
  'Passenger Forward Top',
  'Passenger Forward Middle',
  'Passenger Forward Bottom',
  'Passenger Rear Top',
  'Passenger Rear Middle',
  'Passenger Rear Bottom',
  'No Preference',
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL',
  'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
  'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

const COUNTRIES = [
  'United States', 'Canada', 'United Kingdom', 'Australia',
  'Germany', 'France', 'Spain', 'Italy', 'Japan', 'Brazil', 'Mexico', 'Other',
];

const SIZE_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

const SECTION_LABEL_STYLE = {
  fontFamily: "'Roboto', sans-serif",
  fontWeight: 500,
  fontSize: '11px',
  color: '#565656',
  letterSpacing: '2px',
  textTransform: 'uppercase',
  marginBottom: '16px',
  marginTop: '0',
  paddingBottom: '8px',
  borderBottom: '1px solid #3a3a3a',
};

const EMPTY_CONTACT = { name: '', relationship: '', phone: '', city_state: '', is_primary: false };

function SectionLabel({ children }) {
  return <div style={SECTION_LABEL_STYLE}>{children}</div>;
}

function SaveIndicator({ saved, error }) {
  if (saved) {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#39FF14' }}>
        <CheckCircleOutlined style={{ fontSize: 14, color: '#39FF14' }} />
        <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '11px', color: '#39FF14' }}>Saved</span>
      </span>
    );
  }
  if (error) {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#BF2634' }}>
        <ErrorOutlined style={{ fontSize: 14, color: '#BF2634' }} />
        <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '11px', color: '#BF2634' }}>Error</span>
      </span>
    );
  }
  return null;
}

function FieldLabel({ label, saved, error }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
      <span
        style={{
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 500,
          fontSize: '12px',
          color: '#565656',
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}
      >
        {label}
      </span>
      <SaveIndicator saved={saved} error={error} />
    </div>
  );
}

const INPUT_STYLE = (focused) => ({
  width: '100%',
  backgroundColor: 'transparent',
  border: 'none',
  borderBottom: `1px solid ${focused ? '#39FF14' : '#3a3a3a'}`,
  borderRadius: 0,
  padding: '8px 4px',
  color: '#F5F5F5',
  fontFamily: "'Roboto', sans-serif",
  fontWeight: 400,
  fontSize: '15px',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s ease',
});

function InlineField({ label, fieldName, value, onChange, onBlur, saved, error, fullWidth, invalid }) {
  const [focused, setFocused] = useState(false);
  const borderColor = invalid ? '#BF2634' : (focused ? '#39FF14' : '#3a3a3a');
  return (
    <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
      <FieldLabel label={label} saved={saved} error={error} />
      <input
        type="text"
        value={value || ''}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={(e) => { setFocused(false); onBlur(fieldName, e.target.value); }}
        style={{ ...INPUT_STYLE(focused), borderBottom: `1px solid ${borderColor}` }}
      />
    </div>
  );
}

function InlineSelect({ label, fieldName, value, options, onChange, onBlur, saved, error, fullWidth, placeholder = '—' }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
      <FieldLabel label={label} saved={saved} error={error} />
      <select
        value={value || ''}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={(e) => { setFocused(false); onBlur(fieldName, e.target.value); }}
        style={{
          ...INPUT_STYLE(focused),
          cursor: 'pointer',
          appearance: 'none',
          WebkitAppearance: 'none',
          color: value ? '#F5F5F5' : '#565656',
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23565656' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 4px center',
          paddingRight: '24px',
        }}
      >
        <option value="" style={{ backgroundColor: '#2a2a2a', color: '#565656' }}>{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt} style={{ backgroundColor: '#2a2a2a', color: '#F5F5F5' }}>{opt}</option>
        ))}
      </select>
    </div>
  );
}

function ArrayInput({ fieldName, value, onChange, onBlur, placeholder }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={(e) => { setFocused(false); onBlur(fieldName, e.target.value); }}
      placeholder={placeholder}
      style={INPUT_STYLE(focused)}
    />
  );
}

function ArrayTags({ tags }) {
  if (!tags || tags.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
      {tags.map((tag, i) => (
        <span
          key={i}
          style={{
            backgroundColor: 'rgba(255,255,255,0.06)',
            color: '#888888',
            fontFamily: "'Roboto', sans-serif",
            fontWeight: 500,
            fontSize: '11px',
            padding: '2px 8px',
            borderRadius: '10px',
          }}
        >
          {tag}
        </span>
      ))}
    </div>
  );
}

function ContactFormInput({ value, onChange }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        width: '100%',
        backgroundColor: 'transparent',
        border: 'none',
        borderBottom: `1px solid ${focused ? '#39FF14' : '#3a3a3a'}`,
        padding: '8px 4px',
        color: '#F5F5F5',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 400,
        fontSize: '15px',
        outline: 'none',
        boxSizing: 'border-box',
        marginBottom: '12px',
      }}
    />
  );
}

function ContactFormLabel({ children }) {
  return (
    <div
      style={{
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '12px',
        color: '#565656',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        marginBottom: '4px',
      }}
    >
      {children}
    </div>
  );
}

function AddContactButton({ onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: hovered ? 'rgba(57,255,20,0.08)' : 'transparent',
        border: '1px solid #39FF14',
        color: '#39FF14',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '13px',
        padding: '8px 16px',
        borderRadius: '4px',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        marginBottom: '16px',
      }}
    >
      + Add Emergency Contact
    </button>
  );
}

function ReturnToWorkspaceButton({ onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: hovered ? 'rgba(57,255,20,0.08)' : 'transparent',
        border: '1px solid #39FF14',
        color: '#39FF14',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '14px',
        padding: '10px 20px',
        borderRadius: '4px',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
      }}
    >
      <ArrowBackOutlined style={{ fontSize: 16, color: 'inherit' }} />
      Return to Workspace
    </button>
  );
}

function WorkspaceOption({ workspace, onClick }) {
  const [hovered, setHovered] = useState(false);
  const initials = workspace.name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .slice(0, 2)
    .join('');
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 16px',
        cursor: 'pointer',
        color: hovered ? '#39FF14' : '#F5F5F5',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '14px',
        backgroundColor: hovered ? 'rgba(57,255,20,0.06)' : 'transparent',
        transition: 'all 0.15s ease',
      }}
    >
      <div
        style={{
          width: '32px',
          height: '32px',
          backgroundColor: workspace.color || '#BF2634',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: '12px', color: '#FFFFFF' }}>
          {initials}
        </span>
      </div>
      {workspace.name}
    </div>
  );
}

export default function MyProfile() {
  const { user, token } = useAuth();
  const { workspaces, switchWorkspace } = useWorkspace();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('Identity');
  const [savedFields, setSavedFields] = useState({});
  const [errorFields, setErrorFields] = useState({});
  const [invalidFields, setInvalidFields] = useState({});
  const [showWorkspaceSelector, setShowWorkspaceSelector] = useState(false);
  const [showAddContactForm, setShowAddContactForm] = useState(false);
  const [newContact, setNewContact] = useState(EMPTY_CONTACT);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [contactError, setContactError] = useState(null);

  const formDataRef = useRef(formData);
  const workspaceSelectorRef = useRef(null);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    async function fetchProfile() {
      try {
        const res = await axios.get(
          `${API_BASE}/api/personnel/${user.personnel_profile_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setProfile(res.data);
        setFormData(res.data);
      } catch {
        setError('Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    }
    if (user?.personnel_profile_id) {
      fetchProfile();
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (workspaceSelectorRef.current && !workspaceSelectorRef.current.contains(e.target)) {
        setShowWorkspaceSelector(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function handleBlur(fieldName, value) {
    if (!profile) return;

    let sendValue = value;
    if (fieldName === 'departments' || fieldName === 'show_positions') {
      sendValue = value.split(',').map((s) => s.trim()).filter(Boolean);
    }

    if ((fieldName === 'primary_email' || fieldName === 'secondary_email') && !isValidEmail(value)) {
      setInvalidFields((prev) => ({ ...prev, [fieldName]: true }));
      setErrorFields((prev) => ({ ...prev, [fieldName]: true }));
      setTimeout(() => {
        setInvalidFields((prev) => { const n = { ...prev }; delete n[fieldName]; return n; });
        setErrorFields((prev) => { const n = { ...prev }; delete n[fieldName]; return n; });
      }, 3000);
      return;
    }
    setInvalidFields((prev) => { const n = { ...prev }; delete n[fieldName]; return n; });

    try {
      const res = await axios.put(
        `${API_BASE}/api/personnel/${profile._id}`,
        { [fieldName]: sendValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfile(res.data);
      setFormData(res.data);
      formDataRef.current = res.data;
      setSavedFields((prev) => ({ ...prev, [fieldName]: true }));
      setTimeout(() => setSavedFields((prev) => { const n = { ...prev }; delete n[fieldName]; return n; }), 2000);
    } catch {
      setErrorFields((prev) => ({ ...prev, [fieldName]: true }));
      setTimeout(() => setErrorFields((prev) => { const n = { ...prev }; delete n[fieldName]; return n; }), 3000);
    }
  }

  async function handleCheckboxChange(fieldName, value) {
    if (!profile) return;

    if (fieldName.includes('.')) {
      const [parent, child] = fieldName.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: { ...(prev[parent] || {}), [child]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [fieldName]: value }));
    }

    try {
      const res = await axios.put(
        `${API_BASE}/api/personnel/${profile._id}`,
        { [fieldName]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfile(res.data);
      setFormData(res.data);
      formDataRef.current = res.data;
      setSavedFields((prev) => ({ ...prev, [fieldName]: true }));
      setTimeout(() => setSavedFields((prev) => { const n = { ...prev }; delete n[fieldName]; return n; }), 2000);
    } catch {
      setErrorFields((prev) => ({ ...prev, [fieldName]: true }));
      setTimeout(() => setErrorFields((prev) => { const n = { ...prev }; delete n[fieldName]; return n; }), 3000);
    }
  }

  async function handleSaveContact() {
    if (!newContact.name.trim()) { setContactError('Name is required'); return; }
    setIsSavingContact(true);
    setContactError(null);
    const updatedContacts = [...(profile.emergency_contacts || []), newContact];
    try {
      const res = await axios.put(
        `${API_BASE}/api/personnel/${profile._id}`,
        { emergency_contacts: updatedContacts },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfile(res.data);
      setFormData(res.data);
      formDataRef.current = res.data;
      setShowAddContactForm(false);
      setNewContact(EMPTY_CONTACT);
      setIsSavingContact(false);
    } catch {
      setContactError('Failed to save contact');
      setIsSavingContact(false);
    }
  }

  function setField(key, value) {
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: { ...(prev[parent] || {}), [child]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [key]: value }));
    }
  }

  function getFieldValue(key) {
    if (!formData) return '';
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      return formData[parent]?.[child] ?? '';
    }
    return formData[key] ?? '';
  }

  function getArrayFieldValue(key) {
    if (!formData) return '';
    const val = formData[key];
    if (Array.isArray(val)) return val.join(', ');
    return val || '';
  }

  function getArrayTags(key) {
    if (!formData) return [];
    const val = formData[key];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') return val.split(',').map((s) => s.trim()).filter(Boolean);
    return [];
  }

  if (isLoading) {
    return (
      <div
        style={{
          backgroundColor: '#222222',
          minHeight: '100vh',
          paddingTop: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '16px', color: '#888888' }}>
          Loading your profile...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          backgroundColor: '#222222',
          minHeight: '100vh',
          paddingTop: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 400, fontSize: '16px', color: '#BF2634' }}>
          {error}
        </span>
      </div>
    );
  }

  const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ');
  const avatarLetter = profile?.first_name ? profile.first_name[0].toUpperCase() : '?';
  const contacts = formData?.emergency_contacts || [];

  function F(fieldName, label, fullWidth = false) {
    return (
      <InlineField
        key={fieldName}
        label={label}
        fieldName={fieldName}
        value={String(getFieldValue(fieldName))}
        onChange={(e) => setField(fieldName, e.target.value)}
        onBlur={handleBlur}
        saved={!!savedFields[fieldName]}
        error={!!errorFields[fieldName]}
        invalid={!!invalidFields[fieldName]}
        fullWidth={fullWidth}
      />
    );
  }

  function S(fieldName, label, options, fullWidth = false, placeholder = '—') {
    return (
      <InlineSelect
        key={fieldName}
        label={label}
        fieldName={fieldName}
        value={String(getFieldValue(fieldName))}
        options={options}
        onChange={(e) => setField(fieldName, e.target.value)}
        onBlur={handleBlur}
        saved={!!savedFields[fieldName]}
        error={!!errorFields[fieldName]}
        fullWidth={fullWidth}
        placeholder={placeholder}
      />
    );
  }

  function PhoneF(fieldName, label) {
    return (
      <InlineField
        key={fieldName}
        label={label}
        fieldName={fieldName}
        value={String(getFieldValue(fieldName))}
        onChange={(e) => setField(fieldName, formatPhone(e.target.value))}
        onBlur={handleBlur}
        saved={!!savedFields[fieldName]}
        error={!!errorFields[fieldName]}
      />
    );
  }

  function EmailF(fieldName, label, fullWidth = false) {
    return (
      <InlineField
        key={fieldName}
        label={label}
        fieldName={fieldName}
        value={String(getFieldValue(fieldName))}
        onChange={(e) => setField(fieldName, e.target.value)}
        onBlur={handleBlur}
        saved={!!savedFields[fieldName]}
        error={!!errorFields[fieldName]}
        invalid={!!invalidFields[fieldName]}
        fullWidth={fullWidth}
      />
    );
  }

  function AirportF(fieldName, label) {
    return (
      <InlineField
        key={fieldName}
        label={label}
        fieldName={fieldName}
        value={String(getFieldValue(fieldName))}
        onChange={(e) => setField(fieldName, formatAirportCode(e.target.value))}
        onBlur={handleBlur}
        saved={!!savedFields[fieldName]}
        error={!!errorFields[fieldName]}
      />
    );
  }

  return (
    <div
      style={{
        backgroundColor: '#222222',
        minHeight: '100vh',
        paddingTop: '56px',
      }}
    >
      {/* PAGE HEADER */}
      <div
        style={{
          backgroundColor: '#2a2a2a',
          borderBottom: '1px solid #3a3a3a',
          padding: '32px 48px 0 48px',
        }}
      >
        {/* Header top row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: '24px',
          }}
        >
          {/* LEFT SIDE — Avatar and name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div
              style={{
                width: '72px',
                height: '72px',
                backgroundColor: '#565656',
                border: '3px solid #39FF14',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 900,
                fontSize: '28px',
                color: '#F5F5F5',
                boxShadow: '0 0 20px rgba(57,255,20,0.2)',
                flexShrink: 0,
              }}
            >
              {avatarLetter}
            </div>

            <div>
              <div
                style={{
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 900,
                  fontSize: '32px',
                  color: '#FFFFFF',
                }}
              >
                {fullName || '—'}
              </div>
              <div
                style={{
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 300,
                  fontSize: '18px',
                  color: '#888888',
                  marginTop: '4px',
                }}
              >
                {profile?.title || profile?.primary_email || ''}
              </div>
            </div>
          </div>

          {/* RIGHT SIDE — Return button + badge */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
            <div ref={workspaceSelectorRef} style={{ position: 'relative' }}>
              <ReturnToWorkspaceButton
                onClick={() => {
                  if (workspaces.length === 1) {
                    navigate('/dashboard');
                  } else {
                    setShowWorkspaceSelector((prev) => !prev);
                  }
                }}
              />

              {showWorkspaceSelector && (
                <div
                  style={{
                    position: 'absolute',
                    top: '44px',
                    right: 0,
                    backgroundColor: '#2a2a2a',
                    border: '1px solid #3a3a3a',
                    borderRadius: '6px',
                    minWidth: '220px',
                    padding: '8px 0',
                    zIndex: 100,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  }}
                >
                  <div
                    style={{
                      fontFamily: "'Roboto', sans-serif",
                      fontWeight: 500,
                      fontSize: '11px',
                      color: '#565656',
                      letterSpacing: '2px',
                      padding: '8px 16px 4px 16px',
                    }}
                  >
                    Select Workspace
                  </div>
                  {workspaces.map((ws) => (
                    <WorkspaceOption
                      key={ws._id}
                      workspace={ws}
                      onClick={() => {
                        switchWorkspace(ws);
                        navigate('/dashboard');
                        setShowWorkspaceSelector(false);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {profile?.is_linked && (
              <span
                style={{
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 500,
                  fontSize: '11px',
                  padding: '4px 10px',
                  borderRadius: '10px',
                  backgroundColor: 'rgba(57,255,20,0.1)',
                  color: '#39FF14',
                }}
              >
                ✓ Linked Account
              </span>
            )}
          </div>
        </div>

        {/* TABS ROW */}
        <div style={{ display: 'flex', gap: 0 }}>
          {TABS.map((tab) => (
            <div
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '12px 24px',
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 500,
                fontSize: '14px',
                color: activeTab === tab ? '#39FF14' : '#888888',
                cursor: 'pointer',
                borderBottom: activeTab === tab ? '2px solid #39FF14' : '2px solid transparent',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
            >
              {tab}
            </div>
          ))}
        </div>
      </div>

      {/* TAB CONTENT */}
      <div style={{ padding: '32px 48px', maxWidth: '1200px' }}>

        {/* ---- IDENTITY ---- */}
        {activeTab === 'Identity' && (
          <>
            <SectionLabel>PERSONAL INFO</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 32px', marginBottom: '32px' }}>
              {F('first_name', 'First Name')}
              {F('last_name', 'Last Name')}
              {F('preferred_name', 'Preferred Name')}
              {EmailF('primary_email', 'Primary Email')}
              {PhoneF('cell_phone', 'Cell Phone')}
              {PhoneF('alt_phone', 'Alt Phone')}
              {F('title', 'Title')}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '12px', color: '#565656', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    Company
                  </span>
                </div>
                <div style={{ padding: '8px 4px', color: '#888888', fontFamily: "'Roboto', sans-serif", fontWeight: 400, fontSize: '15px', borderBottom: '1px solid #2a2a2a' }}>
                  {getFieldValue('company_id') || '—'}
                </div>
                <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '11px', color: '#565656', fontStyle: 'italic', marginTop: '4px' }}>
                  Company linking coming soon
                </div>
              </div>
            </div>

            <SectionLabel>ADDRESS</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 32px', marginBottom: '32px' }}>
              {F('address1', 'Address')}
              {F('address2', 'Address 2')}
              {F('city', 'City')}
              {S('state', 'State', US_STATES, false, '— State —')}
              {F('zip', 'Zip')}
              {S('country', 'Country', COUNTRIES, false, '— Country —')}
            </div>

            <SectionLabel>SIZING</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 32px', marginBottom: '32px' }}>
              {S('tshirt_size', 'T-Shirt', SIZE_OPTIONS, false, '— Size —')}
              {S('polo_size', 'Polo', SIZE_OPTIONS, false, '— Size —')}
              {S('button_down_size', 'Button Down', SIZE_OPTIONS, false, '— Size —')}
              {S('jacket_size', 'Jacket', SIZE_OPTIONS, false, '— Size —')}
            </div>

            <SectionLabel>RATES</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 32px', marginBottom: '32px' }}>
              {F('ideal_day_rate', 'Day Rate')}
              {F('ideal_week_rate', 'Week Rate')}
            </div>

            <SectionLabel>ROLES</SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '12px', color: '#565656', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    Departments
                  </span>
                  <SaveIndicator saved={!!savedFields['departments']} error={!!errorFields['departments']} />
                </div>
                <ArrayInput
                  fieldName="departments"
                  value={getArrayFieldValue('departments')}
                  onChange={(e) => setField('departments', e.target.value)}
                  onBlur={handleBlur}
                  placeholder="e.g. Audio, Lighting, Video"
                />
                <ArrayTags tags={getArrayTags('departments')} />
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '12px', color: '#565656', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    Positions
                  </span>
                  <SaveIndicator saved={!!savedFields['show_positions']} error={!!errorFields['show_positions']} />
                </div>
                <ArrayInput
                  fieldName="show_positions"
                  value={getArrayFieldValue('show_positions')}
                  onChange={(e) => setField('show_positions', e.target.value)}
                  onBlur={handleBlur}
                  placeholder="e.g. FOH Engineer, LD"
                />
                <ArrayTags tags={getArrayTags('show_positions')} />
              </div>
            </div>
          </>
        )}

        {/* ---- TRAVEL & LOYALTY ---- */}
        {activeTab === 'Travel & Loyalty' && (
          <>
            <SectionLabel>TRAVEL PREFERENCES</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 32px', marginBottom: '32px' }}>
              {AirportF('home_airport_code', 'Home Airport')}
              {S('air_seat_preference', 'Seat Preference', ['Window', 'Aisle', 'Middle', 'No Preference'])}
              {S('bus_bunk_preference_1', 'Bunk Preference 1', BUNK_OPTIONS)}
              {S('bus_bunk_preference_2', 'Bunk Preference 2', BUNK_OPTIONS)}
            </div>

            <SectionLabel>AIRLINES</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 32px', marginBottom: '32px' }}>
              {F('delta_ff', 'Delta SkyMiles')}
              {F('swa_rr', 'Southwest Rapid Rewards')}
              {F('aa_advantage', 'AA AAdvantage')}
              {F('united_mp', 'United MileagePlus')}
            </div>

            <SectionLabel>HOTELS</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 32px', marginBottom: '32px' }}>
              {F('marriott_bonvoy', 'Marriott Bonvoy')}
              {F('hilton_honors', 'Hilton Honors')}
              {F('alt_hotel_brand', 'Other Hotel Brand')}
              {F('alt_hotel_number', 'Other Hotel Number')}
            </div>

            <SectionLabel>GROUND</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 32px', marginBottom: '32px' }}>
              {F('national_rewards', 'National')}
              {F('enterprise_rewards', 'Enterprise')}
              {F('hertz_rewards', 'Hertz')}
              {F('alt_auto_company', 'Other Company')}
              {F('alt_auto_number', 'Other Number')}
            </div>

            <SectionLabel>CRUISE</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 32px', marginBottom: '32px' }}>
              {F('crown_anchor_society', 'Royal Caribbean')}
              {F('carnival_vifp', 'Carnival VIFP')}
            </div>
          </>
        )}

        {/* ---- DOCUMENTS ---- */}
        {activeTab === 'Documents' && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              paddingTop: '48px',
              gap: '12px',
            }}
          >
            <UploadFileOutlined style={{ fontSize: 48, color: '#3a3a3a' }} />
            <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '16px', color: '#565656' }}>
              Document storage coming soon
            </span>
            <span
              style={{
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 300,
                fontSize: '13px',
                color: '#3a3a3a',
                marginTop: '8px',
                textAlign: 'center',
                maxWidth: '320px',
              }}
            >
              Travel documents, licenses, and passport info will be stored here securely
            </span>
          </div>
        )}

        {/* ---- MEDICAL ---- */}
        {activeTab === 'Medical' && (
          <>
            <div
              style={{
                backgroundColor: 'rgba(229, 192, 75, 0.08)',
                border: '1px solid rgba(229, 192, 75, 0.2)',
                borderRadius: '6px',
                padding: '12px 16px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <LockOutlined style={{ fontSize: 16, color: '#E5C04B', flexShrink: 0 }} />
              <span
                style={{
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 300,
                  fontSize: '13px',
                  color: '#E5C04B',
                  marginLeft: '8px',
                }}
              >
                Medical information is private and only accessible with explicit consent
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 32px' }}>
              {F('medical.blood_type', 'Blood Type')}
              {F('medical.primary_doctor', 'Primary Doctor')}
              {PhoneF('medical.primary_doctor_phone', 'Doctor Phone')}
              {F('medical.allergies', 'Allergies', true)}
              {F('medical.dietary_restrictions', 'Dietary Restrictions', true)}
              {F('medical.current_medications', 'Current Medications', true)}
              {F('medical.medical_conditions', 'Medical Conditions', true)}
            </div>

            <div style={{ marginTop: '24px' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 400,
                  fontSize: '14px',
                  color: '#F5F5F5',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={!!formData?.medical?.covid_vaccinated}
                  onChange={(e) => handleCheckboxChange('medical.covid_vaccinated', e.target.checked)}
                  style={{ accentColor: '#39FF14', width: '16px', height: '16px', cursor: 'pointer' }}
                />
                COVID Vaccinated
              </label>
            </div>
          </>
        )}

        {/* ---- EMERGENCY CONTACTS ---- */}
        {activeTab === 'Emergency Contacts' && (
          <>
            <SectionLabel>EMERGENCY CONTACTS</SectionLabel>

            <AddContactButton onClick={() => setShowAddContactForm(true)} />

            {showAddContactForm && (
              <div
                style={{
                  backgroundColor: '#2a2a2a',
                  border: '1px solid #39FF14',
                  borderRadius: '6px',
                  padding: '20px',
                  marginBottom: '16px',
                }}
              >
                <div>
                  <ContactFormLabel>Name</ContactFormLabel>
                  <ContactFormInput
                    value={newContact.name}
                    onChange={(e) => setNewContact((p) => ({ ...p, name: e.target.value }))}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <ContactFormLabel>Relationship</ContactFormLabel>
                    <ContactFormInput
                      value={newContact.relationship}
                      onChange={(e) => setNewContact((p) => ({ ...p, relationship: e.target.value }))}
                    />
                  </div>
                  <div>
                    <ContactFormLabel>Phone</ContactFormLabel>
                    <ContactFormInput
                      value={newContact.phone}
                      onChange={(e) => setNewContact((p) => ({ ...p, phone: formatPhone(e.target.value) }))}
                    />
                  </div>
                </div>

                <div>
                  <ContactFormLabel>City, State</ContactFormLabel>
                  <ContactFormInput
                    value={newContact.city_state}
                    onChange={(e) => setNewContact((p) => ({ ...p, city_state: e.target.value }))}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontFamily: "'Roboto', sans-serif",
                      fontWeight: 400,
                      fontSize: '14px',
                      color: '#F5F5F5',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={newContact.is_primary}
                      onChange={(e) => setNewContact((p) => ({ ...p, is_primary: e.target.checked }))}
                      style={{ accentColor: '#39FF14', width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    Set as primary contact
                  </label>
                </div>

                {contactError && (
                  <div
                    style={{
                      color: '#BF2634',
                      fontFamily: "'Roboto', sans-serif",
                      fontWeight: 500,
                      fontStyle: 'italic',
                      fontSize: '13px',
                      marginBottom: '8px',
                    }}
                  >
                    {contactError}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                  <button
                    onClick={() => {
                      setShowAddContactForm(false);
                      setNewContact(EMPTY_CONTACT);
                      setContactError(null);
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #565656',
                      color: '#F5F5F5',
                      fontFamily: "'Roboto', sans-serif",
                      fontWeight: 500,
                      fontSize: '13px',
                      padding: '7px 14px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveContact}
                    disabled={isSavingContact}
                    style={{
                      backgroundColor: '#BF2634',
                      color: '#FFFFFF',
                      fontFamily: "'Roboto', sans-serif",
                      fontWeight: 500,
                      fontSize: '13px',
                      padding: '7px 14px',
                      borderRadius: '4px',
                      border: 'none',
                      cursor: isSavingContact ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {isSavingContact ? 'Saving...' : 'Add Contact'}
                  </button>
                </div>
              </div>
            )}

            {contacts.length === 0 && !showAddContactForm ? (
              <div
                style={{
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 300,
                  fontSize: '14px',
                  color: '#565656',
                  marginTop: '16px',
                }}
              >
                No emergency contacts added
              </div>
            ) : (
              <div style={{ marginTop: contacts.length > 0 ? '16px' : '0' }}>
                {contacts.map((contact, i) => (
                  <div
                    key={i}
                    style={{
                      backgroundColor: '#2a2a2a',
                      border: '1px solid #3a3a3a',
                      borderRadius: '6px',
                      padding: '16px',
                      marginBottom: '12px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '15px', color: '#F5F5F5' }}>
                        {contact.name || '—'}
                      </span>
                      {contact.is_primary && (
                        <span
                          style={{
                            fontFamily: "'Roboto', sans-serif",
                            fontWeight: 500,
                            fontSize: '11px',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            backgroundColor: 'rgba(57, 255, 20, 0.1)',
                            color: '#39FF14',
                            marginLeft: '8px',
                          }}
                        >
                          Primary
                        </span>
                      )}
                    </div>
                    {contact.relationship && (
                      <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 400, fontSize: '13px', color: '#888888', marginBottom: '2px' }}>
                        {contact.relationship}
                      </div>
                    )}
                    {contact.phone && (
                      <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 400, fontSize: '13px', color: '#565656', marginBottom: '2px' }}>
                        {contact.phone}
                      </div>
                    )}
                    {contact.city_state && (
                      <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 400, fontSize: '13px', color: '#565656' }}>
                        {contact.city_state}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
